import React, { useEffect, useMemo, useRef, useState } from 'react'
import NumericKeypad from '../../../components/ui-components/NumericKeypad'
import CustomAmountInput from '../../../components/ui-components/CustomAmountInput'
import { useNavigate } from 'react-router-dom'
import { paymentPageConfig } from '../../../utils/mf-execution/paymentPageConfig'
import { FolioDetail, PaymentPageType } from '../../../types/mf-execution.types'
import { useMFExecutionContext } from '../../../state-management/contexts/MFExecutionContext'
import { useWithdrawContext } from '../../../state-management/contexts/WithdrawContext'
import { useMetaDataContext } from '../../../state-management/contexts/MetaDataContext'
import { AllocationAmountAdjustmentToast } from '../../../components/execution/AllocationAmountAdjustmentToast'
import { PaymentPageHeader } from '../../../components/execution/PaymentPageHeader'
import { useInputKeypad } from '../../../hooks/mf-execution/useInputkeypad'
import { useProcessingPartnerMaintenanceBottomSheet } from '../../../hooks/mf-execution/useProcessingPartnerMaintenanceBottomSheet'
import { WithdrawPaymentMethodsButton } from '../../../components/execution/withdraw/WithdrawPaymentMethodsButton'
import { NoBankAccountWithdrawPaymentMethodsButton } from '../../../components/execution/withdraw/NoBankAccountWithdrawPaymentMethodsButton'
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import { WithdrawConfirmationBottomSheet } from '../../../components/BottomSheetComponents/mf-execution/withdraw/WithdrawConfirmationBottomSheet'
import { convertNumberToCurrency, roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import { getEffectiveStepper } from '../../../utils/mf-execution/paymentAmount'
import RenderHTML from '../../../components/ui-components/RenderHTML'
import { AvailableBalanceBottomSheet } from '../../../components/BottomSheetComponents/mf-execution/withdraw/AvailableBalanceBottomSheet'
import { ExitLoadBottomSheet } from '../../../components/BottomSheetComponents/mf-execution/withdraw/ExitLoadBottomSheet'
import { useLoading } from '../../../state-management/contexts/LoadingContext'
import { BreakPoint } from '../../../types/mutual-fund-detail.types'
import { EventNames, UNITS } from '../../../types/misc.types'
import { getBottomPadding } from '../../../utils/sessionStorage'
import { getTopPadding } from '../../../utils/sessionStorage'
import { trackEvent } from '../../../utils/appBridge'
import { hasBasicChecksForWithdrawal } from '../../../utils/mf-execution/withdrawUtils'

enum ErrorType {
  MIN = 'min',
  MAX = 'max',
  STEPPER = 'stepper',
}

const WithdrawPaymentPage: React.FC = () => {
  const { mfExecutionData } = useMFExecutionContext()
  const { showLoading, hideLoading } = useLoading()
  const {
    withdrawMasterPayload,
    handleWithdrawMasterPayload,
    selectedAccount,
    handleAccountSelection,
    selectedFolio,
    amount,
    initialShownAmount,
    handleAmountChange,
    amountAdjustmentReason,
    clearAmountAdjustmentReason,
    isFullWithdrawal,
    bankAccounts,
    handleOTPTrigger,
    calculatedExitLoad,
    taxCalculations,
    isTaxCalculationsLoading,
    handleRefetchTaxCalculations,
    withdrawsToFolioLinkedBank,
    isBankAccountsFetchedAfterMount
  } = useWithdrawContext()
  const [isAllUnits, setIsAllUnits] = useState(false)
  const [isVibratingPill, setIsVibratingPill] = useState(false)
  const [shouldOpenBottomSheet, setShouldOpenBottomSheet] = useState(false)
  const [isInvestedAmountBelowMinWithdrawal, setIsInvestedAmountBelowMinWithdrawal] = useState(false)
  const { openBottomSheet, closeBottomSheet, destroyBottomSheet } = useBottomSheet()
  const navigate = useNavigate()
  const { isin, data: metaData } = useMetaDataContext()
  const fundName = metaData?.fundDetails?.fundName ?? ''
  const trackingFund = isin && fundName ? { fundIsin: isin, fundName } : undefined
  const paymentType = PaymentPageType.WITHDRAW
  const prevEnteredAmount = useRef(0)
  const didPrecheckFullWithdrawalRef = useRef(false)
  const { placeholder, errorHelpText } = paymentPageConfig[paymentType as PaymentPageType]

  useProcessingPartnerMaintenanceBottomSheet('WithdrawPayment')

  const inputRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ScreenName: 'Withdraw Enter amount',
        ...(trackingFund ?? {})
      }
    })
  }, [])

  const getMinOrMaxAmount = (type: 'min' | 'max') => {
    if (!selectedFolio) {
      return type === 'min' ? 0 : Infinity
    }

    const folioData = mfExecutionData?.sell?.folioDetails?.find(
      (folio) => folio?.folioNumber === selectedFolio?.folioNumber
    )

    if (!folioData) {
      return type === 'min' ? 0 : Infinity
    }

    return type === 'min' ? folioData?.minAmount : folioData?.folioAmount
  }

  const minAmount = useMemo(() => {
    return getMinOrMaxAmount('min') ?? 0
  }, [selectedFolio, mfExecutionData?.sell?.folioDetails])

  const maxAmount = useMemo(() => {
    return getMinOrMaxAmount('max') ?? 0
  }, [selectedFolio, mfExecutionData?.sell?.folioDetails])

  const stepper = useMemo(() => {
    if (!selectedFolio) {
      return undefined
    }

    const folioData = mfExecutionData?.sell?.folioDetails?.find(
      (folio) => folio?.folioNumber === selectedFolio?.folioNumber
    )
    return getEffectiveStepper(folioData?.stepper)
  }, [selectedFolio, mfExecutionData?.sell?.folioDetails])

  const {
    handleBackspaceClick,
    handleCursorChange,
    handleNumberClick,
    errorType,
    cursorPos,
    enteredAmount,
    changeAmount,
    triggerValidation
  } = useInputKeypad({
    minAmount: minAmount ?? 0,
    maxAmount: Math.floor(Math.abs(maxAmount ?? 0)),
    stepper,
    defaultAmount: amount,
    validationTrigger: selectedFolio
  })

  // Update Withdraw context when amount changes
  useEffect(() => {
    if (enteredAmount) {
      handleAmountChange(parseFloat(enteredAmount))
    } else {
      handleAmountChange(0)
    }
  }, [enteredAmount])

  useEffect(() => {
    if (amount > 0 &&
      amount === Math.floor(Math.abs(selectedFolio?.folioAmount || 0))) {
      setIsAllUnits(true)
    } else {
      setIsAllUnits(false)
    }
  }, [amount, selectedFolio?.folioAmount])

  useEffect(() => {
    if (amount && selectedFolio?.folioAmount && amount > selectedFolio.folioAmount) {
      setIsVibratingPill(true)
      // Reset vibration state after animation completes (0.6s = 0.3s * 2 iterations)
      const timer = setTimeout(() => {
        setIsVibratingPill(false)
      }, 600)
      return () => clearTimeout(timer)
    } else {
      setIsVibratingPill(false)
    }
  }, [amount, selectedFolio?.folioAmount])

  const mappedExitLoadSlabs = useMemo(() => {
    return (
      taxCalculations?.exitLoadSlabs.map((slab) => {
        return {
          highBreakPoint: {
            quantity: slab?.timeRange?.max ?? 0,
            unit: slab?.timeRange?.unit ?? UNITS.YEAR
          },
          lowBreakPoint: {
            quantity: slab?.timeRange?.min ?? 0,
            unit: slab?.timeRange?.unit ?? UNITS.YEAR
          },
          rate: {
            amount: slab?.rate ?? 0,
            unit: UNITS.PERCENTAGE
          }
        } as BreakPoint
      }) ?? []
    )
  }, [taxCalculations?.exitLoadSlabs])

  // Handle opening bottom sheet when tax calculations are ready
  useEffect(() => {
    if (
      shouldOpenBottomSheet &&
      !isTaxCalculationsLoading &&
      taxCalculations &&
      hasBasicChecksForWithdrawal(selectedFolio, selectedAccount, withdrawsToFolioLinkedBank)
    ) {
      hideLoading()
      setShouldOpenBottomSheet(false)

      openBottomSheet(
        <WithdrawConfirmationBottomSheet
          onDismiss={() => {
            closeBottomSheet()
          }}
          ScreenName='WithdrawPaymentPage'
          amount={amount}
          selectedAccount={selectedAccount}
          selectedFolio={selectedFolio}
          exitLoad={calculatedExitLoad}
          exitLoadSlabs={mappedExitLoadSlabs}
          stcgInfo={taxCalculations?.stcgTaxInfo ?? { gains: 0, investedAmount: 0, taxRate: 0, taxableAmount: 0 }}
          ltcgInfo={taxCalculations?.ltcgTaxInfo ?? { gains: 0, investedAmount: 0, taxRate: 0, taxableAmount: 0 }}
          grandfatheringRuleApplicable={taxCalculations?.grandfatheringRuleApplicable ?? false}
          trackingFund={trackingFund}
          withdrawsToFolioLinkedBank={withdrawsToFolioLinkedBank}
          onPlaceOrder={() => {
            handleFinalAction()
            destroyBottomSheet()
            navigateToOtp()
          }}
        />
      )
    }
  }, [
    shouldOpenBottomSheet,
    isTaxCalculationsLoading,
    taxCalculations,
    amount,
    selectedFolio,
    selectedAccount,
    calculatedExitLoad,
    metaData?.fundDetails?.exitLoad
  ])

  const enableWithdrawAll = () => {
    if (!isAllUnits) {
      prevEnteredAmount.current = parseFloat(enteredAmount) || 0
    }
    changeAmount(Math.floor(Math.abs(selectedFolio?.folioAmount ?? 0)))
    setIsAllUnits(true)
  }

  const handleWithdrawAllClick = () => {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: 'Withdraw Enter amount',
        eventId: 'MFE_ENTER_AMOUNT_WITHDRAW_CTA',
        buttonLabel: 'Withdraw all',
        ...(trackingFund ?? {})
      }
    })
    if (isInvestedAmountBelowMinWithdrawal) return

    if (!isAllUnits) {
      enableWithdrawAll()
      return
    }
    changeAmount(prevEnteredAmount.current || 0)
    setIsAllUnits(false)
  }

  useEffect(() => {
    didPrecheckFullWithdrawalRef.current = false
  }, [selectedFolio?.folioNumber])

  useEffect(() => {
    if (!selectedFolio) return

    const belowMin = (selectedFolio?.folioAmount ?? 0) < (selectedFolio?.minAmount ?? 0)
    setIsInvestedAmountBelowMinWithdrawal(belowMin)

    if (belowMin) {
      enableWithdrawAll()
      return
    }

    if (isFullWithdrawal && !didPrecheckFullWithdrawalRef.current) {
      didPrecheckFullWithdrawalRef.current = true
      enableWithdrawAll()
    }
  }, [selectedFolio?.folioNumber, selectedFolio?.folioAmount, selectedFolio?.minAmount, isFullWithdrawal])

  // Update withdraw master payload when amount changes
  useEffect(() => {
    if (enteredAmount && !errorType) {
      handleWithdrawMasterPayload({
        ...withdrawMasterPayload,
        amount: parseFloat(enteredAmount),
        isAllUnits: isAllUnits
      })
    }
  }, [enteredAmount, errorType, isAllUnits])

  const navigateToOtp = () => {
    navigate(`/mutual-fund/${isin}/payment/${PaymentPageType.WITHDRAW}/otp`)
  }

  const handleFinalAction = () => {
    if (hasBasicChecksForWithdrawal(selectedFolio, selectedAccount, withdrawsToFolioLinkedBank)) {
      handleOTPTrigger({
        accountId: selectedAccount?.accountId ?? '',
        transactionType: 'REDEMPTION',
        isin: isin,
        amount: amount,
        folioNumber: selectedFolio?.folioNumber ?? '',
        isAllUnits: isAllUnits
      })
    }
  }

  const withdrawNow = async () => {
    showLoading()
    await handleRefetchTaxCalculations()
    setShouldOpenBottomSheet(true)
  }

  const handleWithdrawNowClick = () => {
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: 'Withdraw Enter amount',
        buttonLabel: 'Continue',
        eventId: 'MFE_SELECT_AMOUNT_CTA_PAY_NOW',
        ...(trackingFund ?? {})
      }
    })
    if (isInvestedAmountBelowMinWithdrawal) {
      trackContinuedAmount()
      withdrawNow()
      return
    }

    // Trigger validation before proceeding and check result
    const validationError = isInvestedAmountBelowMinWithdrawal ? null : triggerValidation()

    // Check if there are validation errors
    if (validationError) {
      return
    }

    // For withdrawals, we always show confirmation since it's a bank account
    if (!hasBasicChecksForWithdrawal(selectedFolio, selectedAccount, withdrawsToFolioLinkedBank)) {
      return
    }

    trackContinuedAmount()
    withdrawNow()
  }

  const trackContinuedAmount = () => {
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: 'Withdraw Enter amount',
        buttonLabel: 'Continue',
        eventId: 'MFE_SELECT_AMOUNT_CONTINUED',
        amount,
        responseName: initialShownAmount != null ? String(initialShownAmount) : '',
        ...(trackingFund ?? {})
      }
    })
  }

  const getErrorMessage = () => {
    if (!errorType) return null

    switch (errorType) {
      case ErrorType.MIN:
        return `Minimum ${errorHelpText} is ${roundOffAndFormatNumberWithCurrency(minAmount ?? 0, true, 'floor')}`
      case ErrorType.MAX:
        return `Maximum ${errorHelpText} is ${roundOffAndFormatNumberWithCurrency(maxAmount ?? 0, true, 'floor')}`
      case ErrorType.STEPPER:
        return stepper
          ? `Amount must be in multiples of ${convertNumberToCurrency({ amount: stepper ?? 0, casing: 'LOWER' })}`
          : 'Invalid amount'
      default:
        return null
    }
  }

  return (
    <div
      style={{ paddingTop: `calc(${getTopPadding()}px + 16px)`, paddingBottom: `calc(${getBottomPadding()}px + 16px)` }}
      className='w-full h-screen flex flex-col justify-between bg-white px-5'
    >
      <div className='w-full'>
        <PaymentPageHeader />
      </div>

      <div className='w-full flex flex-col justify-center flex-1'>
        <div className='flex flex-col gap-2 w-full'>
          <div className='h-8'>
            <FolioBalancePill
              selectedFolio={selectedFolio}
              isVibrating={isVibratingPill}
              onBalanceClick={() => {
                trackEvent({
                  eventName: EventNames.UserClicked,
                  payload: {
                    ScreenName: 'Withdraw Enter amount',
                    eventId: 'MFE_ENTER_AMOUNT_AVAILABLE_BALANCE',
                    buttonLabel: 'Available balance',
                    ...(trackingFund ?? {})
                  }
                })
                openBottomSheet(
                  <AvailableBalanceBottomSheet
                    onDismiss={closeBottomSheet}
                    ScreenName='WithdrawPaymentPage'
                    trackingFund={trackingFund}
                  />
                )
              }}
            />
          </div>
          <div className='h-[74px] flex items-center w-full animate-fade-150'>
            <CustomAmountInput
              disabledCursor={isInvestedAmountBelowMinWithdrawal}
              value={amount ? `${amount}` : ''}
              cursorPos={cursorPos}
              onCursorChange={handleCursorChange}
              inputRef={inputRef}
              placeholder={placeholder ?? ''}
            />
          </div>
          {isInvestedAmountBelowMinWithdrawal && (
            <div className='text-[#6A6E6E] text-[13px] leading-[150%] font-[400] animate-fade-150 w-[90%]'>
              You can only withdraw full amount as your available amount is lower than {roundOffAndFormatNumberWithCurrency(selectedFolio?.minAmount ?? 0, true, 'floor')} (min. limit)
            </div>
          )}
          {errorType ? (
            <div className='text-[#E77464] text-[14px] font-[500] animate-fade-150'>{getErrorMessage()}</div>
          ) : null}
          <div className='pt-[10px]'>
            <WithdrawBottomBar
              isAllUnits={isAllUnits}
              handleWithdrawAllClick={handleWithdrawAllClick}
              exitLoadSlabs={mappedExitLoadSlabs}
              isInvestedAmountBelowMinWithdrawal={isInvestedAmountBelowMinWithdrawal}
              trackingFund={trackingFund}
            />
          </div>
        </div>
      </div>
      <div className='w-full h-max pt-2 pb-5'>
        <AllocationAmountAdjustmentToast
          amountAdjustmentReason={amountAdjustmentReason}
          amount={amount}
          toastToBeShownById='WithdrawPaymentPage'
          clearAmountAdjustmentReason={clearAmountAdjustmentReason}
          copy='withdrawal'
        />
        <div className='w-full h-[70px]'>
          {isBankAccountsFetchedAfterMount && !withdrawsToFolioLinkedBank && (
            <WithdrawPaymentMethodsButton
              isDisabled={!!errorType || !amount || !selectedFolio}
              handleWithdrawNowClick={handleWithdrawNowClick}
              selectedBankAccount={selectedAccount}
              handleBankAccountChange={handleAccountSelection}
              bankAccounts={bankAccounts ?? []}
              trackingFund={trackingFund}
            />
          )}
          {withdrawsToFolioLinkedBank && (
            <NoBankAccountWithdrawPaymentMethodsButton
              isDisabled={!!errorType || !amount || !selectedFolio}
              handleWithdrawNowClick={handleWithdrawNowClick}
            />
          )}
        </div>
      </div>
      <div className={`w-full px-5 `}>
        {!isInvestedAmountBelowMinWithdrawal && (
          <NumericKeypad onNumberClick={handleNumberClick} onBackspaceClick={handleBackspaceClick} />
        )}
      </div>
    </div>
  )
}

export const FolioBalancePill = ({
  selectedFolio,
  isVibrating,
  onBalanceClick
}: {
  selectedFolio: FolioDetail | null
  isVibrating: boolean
  onBalanceClick: () => void
}) => {
  return (
    <div
      onClick={onBalanceClick}
      className={`rounded-[12px] bg-[#F0F4F7] text-[#6A6E6E] font-[400] text-[13px] pt-1 pb-[6px] pr-2 pl-3 cursor-pointer w-max max-w-full flex items-center gap-[6px] ${isVibrating ? 'animate-pill-vibrate' : ''}`}
    >
      <div>{roundOffAndFormatNumberWithCurrency(selectedFolio?.folioAmount ?? 0, true, 'floor')} available </div>
      <div className='mt-[2px]'>
        <RenderHTML innerHTMLText={InfoIcon} />
      </div>
    </div>
  )
}

export const WithdrawBottomBar = ({
  isAllUnits,
  handleWithdrawAllClick,
  exitLoadSlabs,
  isInvestedAmountBelowMinWithdrawal,
  trackingFund
}: {
  isAllUnits: boolean
  handleWithdrawAllClick: () => void
  exitLoadSlabs: BreakPoint[]
  isInvestedAmountBelowMinWithdrawal: boolean
  trackingFund?: { fundIsin: string; fundName: string }
}) => {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const { amount, calculatedExitLoad } = useWithdrawContext()

  return (
    <div className='flex gap-2 items-center'>
      <div
        test-locator='MFE_ENTER_AMOUNT_WITHDRAW_CTA'
        onClick={handleWithdrawAllClick}
        className={`border border-[#EBF2F2] rounded-[12px] px-3 py-2
      flex items-center gap-2 ${isInvestedAmountBelowMinWithdrawal ? 'opacity-50' : ''}`}
      >
        <div className='text-[#232323] text-[13px] font-[500]'>Withdraw all</div>
        <div className='mt-[1px]'>
          <RenderHTML innerHTMLText={isAllUnits ? CheckedBox : UncheckedBox} />
        </div>
      </div>
      {calculatedExitLoad > 0 && amount > 0 && (
        <div
          onClick={() => {
            trackEvent({
              eventName: EventNames.UserClicked,
              payload: {
                ScreenName: 'Withdraw Enter amount',
                eventId: 'MFE_ENTER_AMOUNT_EXIT_LOAD',
                buttonLabel: 'Exit load',
                ...(trackingFund ?? {})
              }
            })
            openBottomSheet(
              <ExitLoadBottomSheet
                heading='Exit Load'
                onDismiss={closeBottomSheet}
                exitLoad={calculatedExitLoad}
                amount={amount}
                exitLoadSlabs={exitLoadSlabs}
              />
            )
          }}
          className='border border-[#EBF2F2] rounded-[12px] px-3 py-2
      flex items-center gap-2 animate-fade-150'
        >
          <div className='text-[#232323] text-[13px] font-[500]'>
            {roundOffAndFormatNumberWithCurrency(calculatedExitLoad)} Exit load
          </div>
          <div className='mt-[1px]'>
            <RenderHTML innerHTMLText={InfoIcon} />
          </div>
        </div>
      )}
    </div>
  )
}

const InfoIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
  <g clip-path="url(#clip0_47536_21417)">
    <path d="M7 0.5C9.2403 0.5 10.8414 1.06988 11.8857 2.11426C12.9301 3.15864 13.5 4.75971 13.5 7C13.5 9.2403 12.9301 10.8414 11.8857 11.8857C10.8414 12.9301 9.2403 13.5 7 13.5C4.75971 13.5 3.15864 12.9301 2.11426 11.8857C1.06988 10.8414 0.5 9.2403 0.5 7C0.5 4.75971 1.06987 3.15864 2.11426 2.11426C3.15864 1.06987 4.75971 0.5 7 0.5Z" stroke="#8E9393"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M5.58019 8.932C5.25329 8.932 4.98828 9.19049 4.98828 9.50936C4.98828 9.82825 5.25329 10.0867 5.58019 10.0867H8.42137C8.74827 10.0867 9.01328 9.82825 9.01328 9.50936C9.01328 9.19049 8.74827 8.932 8.42137 8.932H7.59269V6.27616C7.59269 5.9573 7.32769 5.69881 7.00078 5.69881H6.05372C5.72682 5.69881 5.46181 5.9573 5.46181 6.27616C5.46181 6.59503 5.72682 6.85352 6.05372 6.85352H6.40887V8.932H5.58019ZM7.94784 3.96674C7.94784 4.47692 7.52382 4.89051 7.00078 4.89051C6.47774 4.89051 6.05372 4.47692 6.05372 3.96674C6.05372 3.45656 6.47774 3.04297 7.00078 3.04297C7.52382 3.04297 7.94784 3.45656 7.94784 3.96674Z" fill="#8E9393"/>
  </g>
  <defs>
    <clipPath id="clip0_47536_21417">
      <rect width="14" height="14" fill="white"/>
    </clipPath>
  </defs>
</svg>`

const UncheckedBox = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
  <rect x="0.75" y="0.75" width="12.5" height="12.5" rx="3.25" stroke="#8E9393" stroke-width="1.5"/>
</svg>`

const CheckedBox = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
  <rect width="14" height="14" rx="4" fill="#1470CC"/>
  <path d="M10.2559 5.85054L6.58935 9.97534C6.51196 10.0624 6.42009 10.1315 6.31898 10.1786C6.21786 10.2257 6.10948 10.25 6.00003 10.25C5.89058 10.25 5.7822 10.2257 5.68109 10.1786C5.57997 10.1315 5.4881 10.0624 5.41072 9.97534L3.7441 8.10043C3.58781 7.9246 3.5 7.68613 3.5 7.43746C3.5 7.1888 3.58781 6.95033 3.7441 6.7745C3.9004 6.59867 4.11238 6.49989 4.33342 6.49989C4.55445 6.49989 4.76644 6.59867 4.92273 6.7745L6.00003 7.98644L9.07727 4.52461C9.15466 4.43755 9.24653 4.36849 9.34765 4.32137C9.44876 4.27425 9.55714 4.25 9.66658 4.25C9.77603 4.25 9.8844 4.27425 9.98552 4.32137C10.0866 4.36849 10.1785 4.43755 10.2559 4.52461C10.3333 4.61167 10.3947 4.71503 10.4366 4.82878C10.4784 4.94253 10.5 5.06445 10.5 5.18758C10.5 5.3107 10.4784 5.43262 10.4366 5.54637C10.3947 5.66012 10.3333 5.76348 10.2559 5.85054Z" fill="white"/>
</svg>`

export default WithdrawPaymentPage
